import React, { useState, useEffect, useRef } from 'react';
import BBALoader from '../components/BBALoader';
import format from 'date-fns/format';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import {
  Users, Activity, Flame, Check, X, Lock, Volume2, Paperclip, Send, Landmark, CreditCard,
  HelpCircle, MessageSquare, Sparkles,
  Hourglass, Map as MapIcon, ChevronRight, Phone, ThumbsUp, MessageCircle, Video,
  CalendarDays, CalendarOff, PhoneCall, ListChecks, Target,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useResponsive } from '../contexts/ResponsiveContext';

import { getTasksForPortalUser, toggleTaskCompletion, getTaskComments, addTaskComment } from '../services/taskService';
import { getEventsByUser } from '../services/eventService';
import { fetchGoogleCalendarEvents } from '../services/googleCalendarService';
import { getUserIntegrationsStatus } from '../services/userIntegrationService';
import { createQuestion } from '../services/firebase/questions';
import { getRecentCommunityPosts } from '../services/firebase/community';
import { getDashboardGoals, getUserGoals } from '../services/firebase/userGoalsService';
import Modal from '../components/Modal';
import SuccessManagerQuestionModal from '../components/SuccessManagerQuestionModal';
import BreakthroughSessionBackground, { getBreakthroughTheme } from '../components/portal/BreakthroughSessionBackground';
import PortalLockedDashboard from '../components/portal/PortalLockedDashboard';
import PortalBackground from '../components/PortalBackground';

import { getInternalUsers } from '../services/userService';
import { getMyAccountSnapshot, findAccountIdByEmail, getAccountById, getCompanyIdForUser, getPrimaryUserIdForAccount } from '../services/firestoreService';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { getSummitEventDetails, getSummitDashboardVisibility } from '../services/summitService';
import { useSummit } from '../contexts/SummitContext';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useSuccessManagerQuestionAssignee } from '../hooks/useSuccessManagerQuestionAssignee';
import { useStrategyCalls } from '../hooks/useStrategyCalls';
import { useTaskTime } from '../hooks/useTaskTime';
import { formatTaskTimeHours } from '../services/taskTimeService';
import { buildDashboardWhatsNewItems, WHATS_NEW_KIND_META } from '../services/whatsNewFeed';
import { BBA_CHECK_IN_CALENDLY_URL, openCalendlyPopup } from '../utils/calendlyWidget';
import clientRoadmapService from '../services/clientRoadmapService';
import {
  getCurrentRoadmapStepTasks,
  ONBOARDING_ROADMAP_ITEMS,
  transformClientRoadmapTemplates,
} from '../utils/roadmapDashboardTasks';
import {
  formatSessionDateLabel,
  formatSessionTimeLabel,
  getBreakthroughTodayStart,
  isSessionInCurrentWeek,
  sessionToStartDate,
} from '../utils/breakthroughSessions';

const BREAKTHROUGH_SESSION_MEET_URL = 'https://meet.google.com/ije-sabd-cxx';

function manualCheckInFollowUpsStorageKey() {
  const month = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return `checkInFollowUpsMonth:${month}`;
}

// Avatar component that shows profile picture or initials
const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const { darkMode } = useTheme();
  
  console.log('UserAvatar received user data:', user);
  
  const sizeClasses = {
    sm: 'w-6 h-6 text-sm',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const baseClasses = `rounded-full flex items-center justify-center font-medium ${sizeClasses[size]} ${className}`;
  
  if (user.profileImage) {
    console.log('UserAvatar using profile image:', user.profileImage);
    return (
      <div className={`${baseClasses} overflow-hidden`}>
        <img 
          src={user.profileImage} 
          alt={user.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            console.log('Profile image failed to load, falling back to initials');
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`} style={{ display: 'none' }}>
          {user.initials}
        </div>
      </div>
    );
  }

  console.log('UserAvatar using initials:', user.initials);
  return (
    <div className={`${baseClasses} ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
      {user.initials}
    </div>
  );
};

const ClientDashboard = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isOnboardingMode, onboardingProgress, startOnboarding } = useOnboarding();
  const { isMobile, isTablet, isDesktop, windowSize } = useResponsive();
  
  
  
  
  // State for user tasks
  const [userTasks, setUserTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  
  // State for user events
  const [userEvents, setUserEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // State for dropdowns and selections
  const [tasksDropdownOpen, setTasksDropdownOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [selectedTaskPeriod, setSelectedTaskPeriod] = useState('This Week');
  const [selectedEventPeriod, setSelectedEventPeriod] = useState('This Week');
  const [roadmapDashboardTasks, setRoadmapDashboardTasks] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);

  // What's New feed (portal updates + new courses/lessons + new document uploads)
  const [whatsNewItems, setWhatsNewItems] = useState([]);
  const [whatsNewLoading, setWhatsNewLoading] = useState(true);
  
  // Helper functions for localStorage persistence
  const getStorageKey = (key) => {
    return currentUser ? `dashboard_${key}_${currentUser.uid}` : null;
  };
  
  const loadUserPreferences = () => {
    if (!currentUser) return;
    
    try {
      const taskPeriodKey = getStorageKey('taskPeriod');
      const eventPeriodKey = getStorageKey('eventPeriod');
      
      if (taskPeriodKey) {
        const savedTaskPeriod = localStorage.getItem(taskPeriodKey);
        if (savedTaskPeriod && ['Today', 'This Week', 'This Month'].includes(savedTaskPeriod)) {
          setSelectedTaskPeriod(savedTaskPeriod);
        }
      }
      
      if (eventPeriodKey) {
        const savedEventPeriod = localStorage.getItem(eventPeriodKey);
        if (savedEventPeriod && ['Today', 'This Week', 'This Month'].includes(savedEventPeriod)) {
          setSelectedEventPeriod(savedEventPeriod);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };
  
  const saveTaskPeriodPreference = (period) => {
    if (!currentUser) return;
    
    try {
      const key = getStorageKey('taskPeriod');
      if (key) {
        localStorage.setItem(key, period);
      }
    } catch (error) {
      console.error('Error saving task period preference:', error);
    }
  };
  
  const saveEventPeriodPreference = (period) => {
    if (!currentUser) return;
    
    try {
      const key = getStorageKey('eventPeriod');
      if (key) {
        localStorage.setItem(key, period);
      }
    } catch (error) {
      console.error('Error saving event period preference:', error);
    }
  };
  
  // State for Task Details Modal
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskOrEvent, setSelectedTaskOrEvent] = useState(null);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  const [taskDetailsError, setTaskDetailsError] = useState(null);
  const [taskDetailsComments, setTaskDetailsComments] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  // Get current user's profile data
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  
  // New comment state
  const [newTaskComment, setNewTaskComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [commentFile, setCommentFile] = useState(null);
  const [commentFileError, setCommentFileError] = useState('');
  
  // State for summit data
  const [summitData, setSummitData] = useState(null);
  const [summitLoading, setSummitLoading] = useState(true);
  const [showSummitCard, setShowSummitCard] = useState(true);

  // State for next breakthrough session (shared with PortalCalendar styling)
  const [nextBreakthroughSession, setNextBreakthroughSession] = useState(null);
  const [timeUntilBreakthrough, setTimeUntilBreakthrough] = useState('');
  const [breakthroughLoading, setBreakthroughLoading] = useState(false);
  // This week's session is marked "not holding" in CRM fulfillment
  const [noBreakthroughThisWeek, setNoBreakthroughThisWeek] = useState(false);
  
  // State for community posts
  const [recentCommunityPosts, setRecentCommunityPosts] = useState([]);
  const [communityPostsLoading, setCommunityPostsLoading] = useState(false);
  
  // State for goals data
  const [goalsData, setGoalsData] = useState({
    revenue: { goal: 0, current: 0, unit: 'usd' },
    leads: { goal: 0, current: 0, unit: 'number' }
  });
  const [goalsLoading, setGoalsLoading] = useState(false);
  
  // Use summit context for registration status
  const { userRegistration: userSummitRegistration, isRegistered, loading: registrationLoading } = useSummit();
  const { isPortalLocked } = usePortalAccess();
  const {
    used: strategyCallsUsed,
    limit: strategyCallsLimit,
    loading: strategyCallsLoading,
    successManager: strategyCallsSuccessManager,
    periodEnd: strategyCallsPeriodEnd,
  } = useStrategyCalls();
  const taskTime = useTaskTime();

  // Helper to pick a profile image URL from an internal user record
  const pickProfilePicUrl = (data) => {
    if (!data) return '';
    return (
      data.profileImage ||
      data.profilePicture ||
      data.photoURL ||
      data.photoUrl ||
      data.avatarUrl ||
      data.avatarURL ||
      ''
    );
  };

  const initialsFor = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // Load next breakthrough session
  useEffect(() => {
    const fetchNextBreakthroughSession = async () => {
      try {
        setBreakthroughLoading(true);
        setNextBreakthroughSession(null);
        setNoBreakthroughThisWeek(false);

        const today = getBreakthroughTodayStart();

        const q = query(
          collection(db, 'breakthroughSessions'),
          where('date', '>=', Timestamp.fromDate(today)),
          orderBy('date', 'asc'),
          limit(6)
        );

        const snap = await getDocs(q);

        // Sessions marked "not holding" in CRM are skipped; if this week's is
        // the one skipped, the card shows "No Class This Week" instead.
        let session = null;
        for (const docSnap of snap.docs) {
          const candidate = { id: docSnap.id, ...docSnap.data() };
          if (candidate.notHeld === true) {
            if (isSessionInCurrentWeek(candidate)) {
              setNoBreakthroughThisWeek(true);
              break;
            }
            continue;
          }
          session = candidate;
          break;
        }

        if (!session) {
          setNextBreakthroughSession(null);
          return;
        }

        if (session.presenterId && !session.presenterPhoto) {
          try {
            const userSnap = await getDoc(doc(db, 'internal_users', session.presenterId));
            if (userSnap.exists()) {
              session = {
                ...session,
                presenterPhoto: pickProfilePicUrl(userSnap.data()) || session.presenterPhoto,
              };
            }
          } catch {
            // ignore presenter photo errors
          }
        }

        setNextBreakthroughSession(session);
      } catch (err) {
        console.error('Error fetching next breakthrough session:', err);
        setNextBreakthroughSession(null);
      } finally {
        setBreakthroughLoading(false);
      }
    };

    fetchNextBreakthroughSession();
  }, []);

  // Countdown for next breakthrough session
  const [breakthroughCountdown, setBreakthroughCountdown] = useState(null);
  useEffect(() => {
    const updateCountdown = () => {
      const start = sessionToStartDate(nextBreakthroughSession);
      if (!start) {
        setTimeUntilBreakthrough('');
        setBreakthroughCountdown(null);
        return;
      }
      const diff = start - new Date();
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const text = days >= 1
          ? `${days}d ${hours}h`
          : `${hours}h ${minutes}m`;
        setTimeUntilBreakthrough(text);
        setBreakthroughCountdown({ days, hours, minutes, live: false });
      } else {
        setTimeUntilBreakthrough('0h 0m');
        // Only show as live for 2 hours after start; beyond that the session is over
        const live = diff > -(2 * 60 * 60 * 1000);
        setBreakthroughCountdown({ days: 0, hours: 0, minutes: 0, live, past: !live });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000 * 60);
    return () => clearInterval(interval);
  }, [nextBreakthroughSession]);

  // Company/account id for shared data (Strategy Calls, Goal Tracking, Success Manager). Null for single-user.
  const [companyId, setCompanyId] = useState(null);
  // Primary user id for the company (for loading company-wide goals); falls back to currentUser.uid when no company.
  const [goalsUserId, setGoalsUserId] = useState(null);

  // Resolve companyId (from user doc or email) and goalsUserId (primary user for company-wide goals)
  useEffect(() => {
    if (!currentUser?.uid) {
      setCompanyId(null);
      setGoalsUserId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (cancelled) return;
        setCompanyId(cid || null);
        const primaryUid = cid ? await getPrimaryUserIdForAccount(cid) : null;
        if (cancelled) return;
        setGoalsUserId(primaryUid || currentUser.uid);
      } catch (err) {
        if (!cancelled) {
          setCompanyId(null);
          setGoalsUserId(currentUser.uid);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email]);

  const { assignee: successManagerQuestionAssignee } = useSuccessManagerQuestionAssignee(
    currentUser,
    companyId
  );

  // State for question modal

  // State for Check In modal
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInSuccessManagerQuestionModalOpen, setCheckInSuccessManagerQuestionModalOpen] =
    useState(false);
  const [checkInData, setCheckInData] = useState({
    overallSatisfaction: null,
    comments: ''
  });
  const [submittingCheckIn, setSubmittingCheckIn] = useState(false);
  const [hasSubmittedThisMonth, setHasSubmittedThisMonth] = useState(false);
  const [checkingSubmissionStatus, setCheckingSubmissionStatus] = useState(true);
  const [calUserProfile, setCalUserProfile] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!cancelled && userDoc.exists()) setCalUserProfile(userDoc.data());
      } catch (e) {
        console.error('ClientDashboard: load user for Calendly prefill', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  const persistManualCheckInFollowUp = (patch) => {
    const key = manualCheckInFollowUpsStorageKey();
    let o = {};
    try {
      o = JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch (_) {
      o = {};
    }
    sessionStorage.setItem(key, JSON.stringify({ ...o, ...patch }));
  };

  const openManualCheckInCalendly = () => {
    const firstName =
      calUserProfile?.firstName?.trim() ||
      currentUser?.displayName?.split(/\s+/)[0] ||
      currentUser?.email?.split('@')[0] ||
      '';
    const lastName =
      calUserProfile?.lastName?.trim() ||
      (currentUser?.displayName?.split(/\s+/)?.slice(1)?.join(' ') || '').trim() ||
      '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const prefill =
      currentUser || calUserProfile
        ? {
            firstName,
            lastName,
            name: fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
            email: currentUser?.email || '',
            phone: calUserProfile?.phone?.trim() || '',
          }
        : {};
    const hasPrefill =
      prefill.name || prefill.firstName || prefill.lastName || prefill.email || prefill.phone;
    openCalendlyPopup(BBA_CHECK_IN_CALENDLY_URL, hasPrefill ? prefill : undefined);
  };

  const handleCheckInSuccessManagerQuestionSubmit = async (questionText) => {
    if (!currentUser) {
      alert('You must be logged in to submit a question.');
      return;
    }
    if (!questionText?.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!successManagerQuestionAssignee?.id) {
      alert('No Success Manager is assigned to your account yet. Please contact support.');
      return;
    }
    try {
      const questionData = {
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: successManagerQuestionAssignee.id,
        assigneeName: successManagerQuestionAssignee.name,
        questionText: questionText.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        pillar: 'success_manager',
      };
      await createQuestion(questionData);
      persistManualCheckInFollowUp({ wantsSuccessManagerQuestion: true });
    } catch (error) {
      console.error('Error submitting Success Manager question:', error);
      alert('Failed to submit question. Please try again.');
    }
  };
  


  
  // Handle location state for tasks instruction and onboarding mode
  useEffect(() => {
    if (location.state?.showTasksInstruction) {
      setShowTasksInstruction(true);
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true });
    }
    
    // Auto-start onboarding mode for new users
    if (location.state?.startOnboarding && currentUser && !isOnboardingMode) {
      console.log('🔄 Auto-starting onboarding mode for new user:', currentUser.uid);
      startOnboarding(currentUser.uid);
      // Clear the state to prevent restarting on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, currentUser, isOnboardingMode, startOnboarding]);
  
  // State for tasks instruction message
  const [showTasksInstruction, setShowTasksInstruction] = useState(false);
  
  // Ref for scrolling to top of dashboard
  const dashboardTopRef = useRef(null);
  
  // Helper function to get revenue metrics from real data
  const getRevenueMetrics = () => {
    return salesData || goalsData.revenue;
  };

  // Generate graph data based on real sales and marketing data
  const generateGraphData = () => {
    // Use real data if available, otherwise fall back to goals data
    const currentRevenue = salesData?.current || goalsData.revenue.current;
    const revenueGoal = salesData?.goal || goalsData.revenue.goal;
    const currentLeads = marketingData?.current || goalsData.leads.current;
    const leadsGoal = marketingData?.goal || goalsData.leads.goal;
    
    // Generate quarterly data for current year
    return [
      { 
        name: 'Q1', 
        revenue: Math.round(currentRevenue * 0.25), 
        revenueGoal: Math.round(revenueGoal * 0.25), 
        leads: Math.round(currentLeads * 0.25), 
        leadsGoal: Math.round(leadsGoal * 0.25) 
      },
      { 
        name: 'Q2', 
        revenue: Math.round(currentRevenue * 0.5), 
        revenueGoal: Math.round(revenueGoal * 0.5), 
        leads: Math.round(currentLeads * 0.5), 
        leadsGoal: Math.round(leadsGoal * 0.5) 
      },
      { 
        name: 'Q3', 
        revenue: Math.round(currentRevenue * 0.75), 
        revenueGoal: Math.round(revenueGoal * 0.75), 
        leads: Math.round(currentLeads * 0.75), 
        leadsGoal: Math.round(leadsGoal * 0.75) 
      },
      { 
        name: 'Q4', 
        revenue: currentRevenue, 
        revenueGoal: revenueGoal, 
        leads: currentLeads, 
        leadsGoal: leadsGoal 
      }
    ];
  };



  // Helper function to format time ago
  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown time';
    
    const now = new Date();
    const postDate = new Date(date);
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return postDate.toLocaleDateString();
  };

  // Helper function to convert community posts to activity format
  const getCommunityActivity = () => {
    return recentCommunityPosts.map(post => ({
      id: post.id,
      user: post.author || 'Unknown User',
      action: 'posted',
      item: post.title || 'Community Post',
      time: formatTimeAgo(post.createdAt),
      type: post.type || 'general',
      category: post.category || 'general'
    }));
  };


  // Helper function to format the value based on the unit
  const formatValue = (value, unit) => {
    if (unit === 'usd') {
      return `$${value.toLocaleString()}`;
    }
    return value;
  };

  // Helper function to calculate days until summit
  const getDaysUntilSummit = () => {
    if (!summitData?.startDate) return null;
    
    const today = new Date();
    const summitDate = new Date(summitData.startDate);
    const timeDiff = summitDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  // Helper function to format summit dates
  const formatSummitDates = () => {
    if (!summitData?.startDate || !summitData?.endDate) return 'Sep 16-19';
    
    const startDate = new Date(summitData.startDate);
    const endDate = new Date(summitData.endDate);
    
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    
    return `${startMonth} ${startDay}-${endDay}`;
  };

  // Fetch summit data
  const fetchSummitData = async () => {
    try {
      setSummitLoading(true);
      const [data, visible] = await Promise.all([
        getSummitEventDetails(),
        getSummitDashboardVisibility()
      ]);
      setSummitData(data);
      setShowSummitCard(visible);
    } catch (err) {
      console.error('Error fetching summit data:', err);
    } finally {
      setSummitLoading(false);
    }
  };

  // Fetch recent community posts
  const fetchRecentCommunityPosts = async () => {
    try {
      setCommunityPostsLoading(true);
      const posts = await getRecentCommunityPosts(3);
      setRecentCommunityPosts(posts);
    } catch (err) {
      console.error('Error fetching recent community posts:', err);
    } finally {
      setCommunityPostsLoading(false);
    }
  };

  // Fetch dashboard goals data (company-wide: uses goalsUserId so all company members see same data)
  const fetchDashboardGoals = async () => {
    const uid = goalsUserId || currentUser?.uid;
    if (!uid) return;

    try {
      setGoalsLoading(true);
      const goals = await getDashboardGoals(uid);
      setGoalsData(goals);
    } catch (err) {
      console.error('Error fetching dashboard goals:', err);
    } finally {
      setGoalsLoading(false);
    }
  };



  // Fetch user tasks
  const fetchUserTasks = async () => {
    if (!currentUser) return;
    
    try {
      setTasksLoading(true);
      let clientAccountId = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          clientAccountId = userDoc.data()?.accountId || null;
        }
      } catch (_) {
        // ignore
      }
      const tasks = await getTasksForPortalUser(currentUser.uid, clientAccountId);
      setUserTasks(tasks);
    } catch (err) {
      console.error('Error fetching user tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchRoadmapDashboardTasks = async () => {
    if (!currentUser) {
      setRoadmapDashboardTasks([]);
      setRoadmapItems([]);
      return;
    }

    try {
      if (isOnboardingMode) {
        setRoadmapItems(ONBOARDING_ROADMAP_ITEMS);
        setRoadmapDashboardTasks(
          getCurrentRoadmapStepTasks(ONBOARDING_ROADMAP_ITEMS, currentUser.uid, {
            useFirstWhenNoInProgress: true,
          })
        );
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        setRoadmapDashboardTasks([]);
        setRoadmapItems([]);
        return;
      }

      const accountId = userDoc.data()?.accountId;
      if (!accountId) {
        setRoadmapDashboardTasks([]);
        setRoadmapItems([]);
        return;
      }

      const clientRoadmap = await clientRoadmapService.getClientRoadmap(accountId);
      const items = transformClientRoadmapTemplates(clientRoadmap);
      setRoadmapItems(items);
      setRoadmapDashboardTasks(getCurrentRoadmapStepTasks(items, currentUser.uid));
    } catch (err) {
      console.error('Error fetching roadmap dashboard tasks:', err);
      setRoadmapDashboardTasks([]);
      setRoadmapItems([]);
    }
  };

  // Load the What's New feed by merging every portal department's own What's New
  // feed (Sales, Operations, Technology, Finance, HR, Marketing) and showing the
  // most recent items overall.
  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    (async () => {
      setWhatsNewLoading(true);
      const items = await buildDashboardWhatsNewItems({ currentUser, companyId, maxItems: 8 });
      if (cancelled) return;
      setWhatsNewItems(items);
      setWhatsNewLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, companyId]);

  // Fetch user events - fetch client events like ClientOverview does
  const fetchUserEvents = async () => {
    if (!currentUser) return;
    
    try {
      setEventsLoading(true);
      
      // Get client email from user profile
      let clientEmail = null;
      if (currentUserProfile?.email) {
        clientEmail = currentUserProfile.email.toLowerCase();
      } else if (currentUser?.email) {
        clientEmail = currentUser.email.toLowerCase();
      }
      
      // Get date range (past 6 months to future 6 months, like PortalCalendar)
      const now = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 6);
      
      const allEvents = [];
      
      // Fetch local events assigned to the user (with date filtering)
      const localEvents = await getEventsByUser(currentUser.uid);
      const filteredLocalEvents = localEvents.filter(event => {
        const eventStart = event.startTime || event.start;
        if (!eventStart) return false;
        const eventDate = convertToDate(eventStart);
        if (!eventDate) return false;
        return eventDate >= startDate && eventDate <= endDate;
      });
      allEvents.push(...filteredLocalEvents);
      console.log('Local events fetched (filtered by date):', filteredLocalEvents);
      
      // Fetch Google Calendar events from internal users where client is an attendee (like ClientOverview)
      if (clientEmail) {
        try {
          const internalUsersList = await getInternalUsers();
          
          for (const user of internalUsersList) {
            try {
              const events = await fetchGoogleCalendarEvents(user.id, startDate, endDate);
              
              // Filter events where client email is in attendees
              const clientEvents = events.filter(event => {
                const attendees = event.attendees || [];
                return attendees.some(attendee => 
                  attendee.email?.toLowerCase() === clientEmail
                );
              });
              
              allEvents.push(...clientEvents);
            } catch (userError) {
              console.error(`Error fetching events for user ${user.id}:`, userError);
              // Continue with other users
            }
          }
          
          console.log('Google Calendar events fetched for client:', allEvents.length - filteredLocalEvents.length);
        } catch (googleError) {
          console.error('Error fetching Google Calendar events:', googleError);
          // Don't fail the entire function if Google Calendar fails
        }
      }
      
      // Remove duplicates based on event ID
      const uniqueEvents = allEvents.filter((event, index, self) => 
        index === self.findIndex(e => e.id === event.id)
      );
      
      // Sort events by start time
      uniqueEvents.sort((a, b) => {
        const aTime = convertToDate(a.startTime || a.start);
        const bTime = convertToDate(b.startTime || b.start);
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return aTime - bTime;
      });
      
      console.log('All events (merged and filtered):', uniqueEvents);
      setUserEvents(uniqueEvents);
    } catch (err) {
      console.error('Error fetching user events:', err);
    } finally {
      setEventsLoading(false);
    }
  };


  // Helper function to safely convert Firestore Timestamp to Date
  const convertToDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue?.toDate) {
      return dateValue.toDate();
    }
    if (dateValue instanceof Date) {
      return dateValue;
    }
    return new Date(dateValue);
  };

  // Filter tasks based on selected timeframe
  const filterTasksByTimeframe = (tasks, timeframe) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    endOfWeek.setHours(23, 59, 59, 999); // End of week
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999); // End of month

    const filtered = tasks.filter(task => {
      // If no due date, include in all timeframes (show undated tasks)
      if (!task.dueDate) {
        return true;
      }
      
      // Handle Firestore Timestamp objects
      let dueDate;
      if (task.dueDate?.toDate) {
        dueDate = task.dueDate.toDate();
      } else if (task.dueDate instanceof Date) {
        dueDate = task.dueDate;
      } else {
        dueDate = new Date(task.dueDate);
      }
      
      // Reset time to start of day for comparison
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
      
      switch (timeframe) {
        case 'Today':
          // Include tasks due today or overdue
          return dueDateOnly <= tomorrow;
        case 'This Week':
          // Include tasks due this week or overdue
          return dueDateOnly <= endOfWeek;
        case 'This Month':
          // Include tasks due this month or overdue
          return dueDateOnly <= endOfMonth;
        default:
          return true;
      }
    }).sort((a, b) => {
      // Sort by completion status first, then by due date
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      
      // Handle Firestore Timestamp objects for sorting
      const aDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
      const bDate = b.dueDate?.toDate ? b.dueDate.toDate() : new Date(b.dueDate);
      return aDate - bDate;
    });
    
    console.log(`Filtered tasks for ${timeframe}:`, filtered.length, 'out of', tasks.length);
    return filtered;
  };

  // Filter events based on selected timeframe
  const filterEventsByTimeframe = (events, timeframe) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    endOfWeek.setHours(23, 59, 59, 999); // End of week
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999); // End of month

    console.log(`Filtering events for ${timeframe}:`, events.length, 'total events');
    console.log('Date ranges:', { today, tomorrow, endOfWeek, endOfMonth });
    console.log('All events before filtering:', events);

    const filtered = events.filter(event => {
      console.log('Processing event:', event);
      // Handle both startTime and start properties (for Google Calendar events)
      const eventTimeRaw = event.startTime || event.start;
      console.log('Event time raw:', eventTimeRaw, 'from event:', { startTime: event.startTime, start: event.start });
      
      if (!eventTimeRaw) {
        console.log('Event missing startTime/start:', event);
        return false;
      }
      
      const eventDate = convertToDate(eventTimeRaw);
      console.log('Event date converted:', eventDate, 'from raw:', eventTimeRaw);
      
      if (!eventDate) {
        console.log('Event date conversion failed:', event, eventTimeRaw);
        return false;
      }
      
      // Normalize event date to start of day for comparison
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfWeekOnly = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
      const endOfMonthOnly = new Date(endOfMonth.getFullYear(), endOfMonth.getMonth(), endOfMonth.getDate());
      
      // Exclude past events - only show upcoming events
      if (eventDate < now) {
        return false;
      }

      let matches = false;
      switch (timeframe) {
        case 'Today':
          // Show upcoming events from today only
          matches = eventDateOnly >= todayOnly && eventDateOnly < tomorrow;
          break;
        case 'This Week':
          // Show upcoming events from this week only
          const startOfWeek = new Date(todayOnly);
          startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay()); // Start of week (Sunday)
          matches = eventDateOnly >= startOfWeek && eventDateOnly <= endOfWeekOnly;
          break;
        case 'This Month':
          // Show upcoming events from this month only
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          matches = eventDateOnly >= startOfMonth && eventDateOnly <= endOfMonthOnly;
          break;
        default:
          matches = true;
      }
      
      return matches;
    }).sort((a, b) => {
      const aTime = convertToDate(a.startTime || a.start);
      const bTime = convertToDate(b.startTime || b.start);
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime - bTime;
    });
    
    console.log(`Filtered events for ${timeframe}:`, filtered.length, 'out of', events.length);
    return filtered;
  };

  // Fetch tasks when component mounts or user changes
  useEffect(() => {
    fetchUserTasks();
    fetchRoadmapDashboardTasks();
  }, [currentUser, isOnboardingMode]);

  // Fetch events when component mounts or user/profile changes
  useEffect(() => {
    fetchUserEvents();
  }, [currentUser, currentUserProfile]);

  // Fetch summit data when component mounts
  useEffect(() => {
    fetchSummitData();
  }, []);

  // Fetch recent community posts when component mounts
  useEffect(() => {
    fetchRecentCommunityPosts();
  }, []);

  // Fetch dashboard goals when component mounts or user / company goals scope changes (company-wide)
  useEffect(() => {
    fetchDashboardGoals();
  }, [currentUser, goalsUserId]);

  // Load user preferences when component mounts or user changes
  useEffect(() => {
    loadUserPreferences();
  }, [currentUser]);

  // Check if user has already submitted check-in for current month
  useEffect(() => {
    const checkIfSubmittedThisMonth = async () => {
      if (!currentUser?.email) {
        setCheckingSubmissionStatus(false);
        return;
      }

      try {
        setCheckingSubmissionStatus(true);
        const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const checkInsRef = collection(db, 'check_ins');
        // By uid, not email: clients may only read check-ins they wrote or their company's
        const q = query(
          checkInsRef,
          where('userId', '==', currentUser.uid),
          where('month', '==', currentMonth)
        );
        const querySnapshot = await getDocs(q);
        setHasSubmittedThisMonth(!querySnapshot.empty);
      } catch (error) {
        console.error('Error checking check-in submission status:', error);
        setHasSubmittedThisMonth(false);
      } finally {
        setCheckingSubmissionStatus(false);
      }
    };

    checkIfSubmittedThisMonth();
  }, [currentUser?.email]);

  // Check if today is the 1st of the month
  const isFirstOfMonth = () => {
    const today = new Date();
    return today.getDate() === 1;
  };

  // Determine if check-in card should be shown
  const shouldShowCheckInCard = () => {
    if (checkingSubmissionStatus) return false;
    if (!isFirstOfMonth()) return false;
    if (hasSubmittedThisMonth) return false;
    return true;
  };

  // Fetch sales and marketing data for current year insights
  const [salesData, setSalesData] = useState(null);
  const [marketingData, setMarketingData] = useState(null);
  const [gpmData, setGpmData] = useState(null);
  const [netProfitData, setNetProfitData] = useState(null);
  const [salesMarketingLoading, setSalesMarketingLoading] = useState(false);
  
  // State for goal selection popup
  const [showGoalSelectionModal, setShowGoalSelectionModal] = useState(false);
  const [availableGoals, setAvailableGoals] = useState({});
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [activeTab, setActiveTab] = useState('Sales');

  // State for Strategy Calls purchase modal
  const [showStrategyCallsPurchaseModal, setShowStrategyCallsPurchaseModal] = useState(false);
  const [strategyCallsQuantity, setStrategyCallsQuantity] = useState(1);
  const [strategyCallsCheckoutLoading, setStrategyCallsCheckoutLoading] = useState(false);
  const [strategyCallsCheckoutError, setStrategyCallsCheckoutError] = useState(null);
  
  // Load selected goals from Firebase (company-wide: use goalsUserId so all company members see same pinned goals)
  useEffect(() => {
    const loadPinnedGoals = async () => {
      const uid = goalsUserId || currentUser?.uid;
      if (!uid) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const pinnedGoals = (userData.pinnedGoals || []).slice(0, 3);
          setSelectedGoals(pinnedGoals);
        }
      } catch (err) {
        console.error('Error loading pinned goals from Firebase:', err);
        if (currentUser) {
          const savedGoals = localStorage.getItem(`selectedGoals_${currentUser.uid}`);
          if (savedGoals) {
            try {
              const parsedGoals = JSON.parse(savedGoals).slice(0, 3);
              setSelectedGoals(parsedGoals);
            } catch (localErr) {
              console.error('Error loading saved goals from localStorage:', localErr);
            }
          }
        }
      }
    };

    loadPinnedGoals();
  }, [currentUser, goalsUserId]);

  const handlePurchaseStrategyCalls = async (paymentMethod) => {
    if (!currentUser?.email || strategyCallsQuantity < 1 || !paymentMethod) return;
    setStrategyCallsCheckoutError(null);
    setStrategyCallsCheckoutLoading(true);
    try {
      const checkoutCompanyId = companyId || await findAccountIdByEmail(currentUser.email, 'Clients');
      const createSession = httpsCallable(functions, 'createStrategyCallCheckoutSession');
      const isPortal = location.pathname.startsWith('/portal');
      const returnPath = isPortal ? '/portal/checkout/strategy-calls/return' : '/start/checkout/strategy-calls/return';

      const { data } = await createSession({
        quantity: strategyCallsQuantity,
        paymentMethod,
        customerEmail: currentUser.email,
        returnPath,
        origin: window.location.origin,
        ...(checkoutCompanyId && { companyId: checkoutCompanyId })
      });

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setStrategyCallsCheckoutError('No checkout URL received');
        setStrategyCallsCheckoutLoading(false);
      }
    } catch (err) {
      console.error('createCheckoutSession error:', err);
      setStrategyCallsCheckoutError(err.message || 'Failed to start checkout');
      setStrategyCallsCheckoutLoading(false);
    }
  };

  const fetchSalesAndMarketingData = async () => {
    const uid = goalsUserId || currentUser?.uid;
    if (!uid) return;

    try {
      setSalesMarketingLoading(true);
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Get sales data
        const sales = userData.salesData || {};
        if (sales.currentYearProjection && sales.chartData) {
          setSalesData({
            current: sales.currentYearProjection,
            goal: sales.currentYearProjection, // Use current as goal for now
            chartData: sales.chartData
          });
        }
        
        // Get marketing data - calculate current year leads from entries
        const marketingLeads = userData.marketingLeads || [];
        const currentYear = new Date().getFullYear();
        const currentYearLeads = marketingLeads
          .filter(entry => entry.year === currentYear)
          .reduce((sum, entry) => sum + entry.count, 0);
        
        if (marketingLeads.length > 0) {
          setMarketingData({
            current: currentYearLeads,
            goal: 200, // Default goal, could be made configurable
            entries: marketingLeads
          });
        }
      }

      // Get GPM and Net Profit goals from userGoals collection (company-wide via uid)
      const goalsData = await getDashboardGoals(uid);
      const userGoals = await getUserGoals(uid);
      
      // Store all goals for the selection modal
      setAvailableGoals(userGoals);
      
      // Find goals for data setting (reuse variables for GPM and Net Profit)
      const revenueGoal = userGoals.Sales?.find(goal => 
        goal.metricName.toLowerCase().includes('revenue')
      );
      
      const leadsGoal = userGoals.Marketing?.find(goal => 
        goal.metricName.toLowerCase().includes('leads')
      );
      
      const gpmGoal = userGoals.Operations?.find(goal => 
        goal.metricName.toLowerCase().includes('gross profit margin')
      );
      
      const netProfitGoal = userGoals.Finance?.find(goal => 
        goal.metricName.toLowerCase().includes('net profit')
      );
      
      // Set GPM data if goal exists
      if (gpmGoal) {
        setGpmData({
          current: gpmGoal.actual || 0,
          goal: gpmGoal.target || 100,
          unit: gpmGoal.unit || 'percent'
        });
      }
      
      // Set Net Profit data if goal exists
      if (netProfitGoal) {
        setNetProfitData({
          current: netProfitGoal.actual || 0,
          goal: netProfitGoal.target || 100,
          unit: netProfitGoal.unit || 'usd'
        });
      }
    } catch (err) {
      console.error('Error fetching sales and marketing data:', err);
    } finally {
      setSalesMarketingLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesAndMarketingData();
  }, [currentUser, goalsUserId]);

  // Set default goals after data is loaded and no goals are selected
  useEffect(() => {
    if (Object.keys(availableGoals).length > 0 && selectedGoals.length === 0) {
      const defaultGoals = [];
      
      // Try to find common goals to pin by default
      const revenueGoal = availableGoals.Sales?.find(goal => 
        goal.metricName.toLowerCase().includes('revenue')
      );
      if (revenueGoal) defaultGoals.push(`sales_${revenueGoal.id}`);
      
      const leadsGoal = availableGoals.Marketing?.find(goal => 
        goal.metricName.toLowerCase().includes('leads')
      );
      if (leadsGoal) defaultGoals.push(`marketing_${leadsGoal.id}`);
      
      const gpmGoal = availableGoals.Operations?.find(goal => 
        goal.metricName.toLowerCase().includes('gross profit margin')
      );
      if (gpmGoal) defaultGoals.push(`operations_${gpmGoal.id}`);
      
      const netProfitGoal = availableGoals.Finance?.find(goal => 
        goal.metricName.toLowerCase().includes('net profit')
      );
      if (netProfitGoal) defaultGoals.push(`finance_${netProfitGoal.id}`);
      
      if (defaultGoals.length > 0) {
        setSelectedGoals(defaultGoals.slice(0, 4));
      } else {
        // Fallback: if we have any goals, select the first 4
        const allGoals = [];
        Object.keys(availableGoals).forEach(dept => {
          if (availableGoals[dept] && availableGoals[dept].length > 0) {
            availableGoals[dept].forEach(goal => {
              allGoals.push(`${dept.toLowerCase()}_${goal.id}`);
            });
          }
        });
        if (allGoals.length > 0) {
          setSelectedGoals(allGoals.slice(0, 4));
        }
      }
    }
  }, [availableGoals, selectedGoals.length]);



  // Fetch users and accounts for task details
  useEffect(() => {
    const fetchUsersAndAccounts = async () => {
      try {
        const [users, accountsData] = await Promise.all([
          getInternalUsers(),
          getMyAccountSnapshot()
        ]);
        setInternalUsers(users);
        // Only the client's own account: task assignees are staff or this company
        setAccounts(accountsData.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching users and accounts:', err);
      }
    };
    fetchUsersAndAccounts();
  }, []);

  useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        // Try to get from users collection first (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUserProfile(userData);
          return;
        }
        
        // If not found, try internal_users collection (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          console.log('Internal user profile data:', internalUserData);
          setCurrentUserProfile(internalUserData);
        }
      } catch (err) {
        console.error('Error fetching current user profile:', err);
      }
    };
    
    fetchCurrentUserProfile();
  }, [currentUser]);

  // Build the dashboard task list — all incomplete tasks, soonest due first (no period filter)
  useEffect(() => {
    const portalTasks = userTasks.filter((task) => !task.completed);
    const combined = [...roadmapDashboardTasks, ...portalTasks];
    combined.sort((a, b) => {
      const aDue = convertToDate(a.dueDate);
      const bDue = convertToDate(b.dueDate);
      if (!aDue && !bDue) return 0;
      if (!aDue) return 1;
      if (!bDue) return -1;
      return aDue - bDue;
    });
    setFilteredTasks(combined);
  }, [userTasks, roadmapDashboardTasks]);

  // Build the upcoming-calls list — soonest first, no period filter
  useEffect(() => {
    const now = new Date();
    const sorted = [...userEvents].sort((a, b) => {
      const aTime = convertToDate(a.startTime || a.start);
      const bTime = convertToDate(b.startTime || b.start);
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime - bTime; // Soonest first
    });
    const upcoming = sorted.filter((e) => {
      const t = convertToDate(e.startTime || e.start);
      return t && t >= now;
    });
    // Prefer upcoming events; fall back to the full list when nothing is upcoming
    setFilteredEvents(upcoming.length > 0 ? upcoming : sorted);
  }, [userEvents]);

  const toggleTasksDropdown = () => {
    setTasksDropdownOpen(!tasksDropdownOpen);
    if (eventsDropdownOpen) setEventsDropdownOpen(false);
  };

  const toggleEventsDropdown = () => {
    setEventsDropdownOpen(!eventsDropdownOpen);
    if (tasksDropdownOpen) setTasksDropdownOpen(false);
  };

  const handleTaskPeriodSelect = (period) => {
    setSelectedTaskPeriod(period);
    saveTaskPeriodPreference(period);
    setTasksDropdownOpen(false);
  };

  const handleEventPeriodSelect = (period) => {
    setSelectedEventPeriod(period);
    saveEventPeriodPreference(period);
    setEventsDropdownOpen(false);
  };

  const handleTaskCompletion = async (taskId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const roadmapTask = roadmapDashboardTasks.find((t) => t.id === taskId);
    if (roadmapTask?.isRoadmapTask) {
      navigate('/portal/roadmap');
      return;
    }
    
    try {
      const task = userTasks.find(t => t.id === taskId);
      if (!task) return;

      // Update the task completion status
      const updatedTask = { ...task, completed: !task.completed };
      setUserTasks(prevTasks => 
        prevTasks.map(t => t.id === taskId ? updatedTask : t)
      );
      
      // Call the actual update function from taskService
      await toggleTaskCompletion(taskId, !task.completed);
    } catch (err) {
      console.error('Error toggling task completion:', err);
    }
  };




  // Handle click for task or event
  const handleTaskOrEventClick = async (item, type = 'task') => {
    if (type === 'task' && item.isRoadmapTask) {
      navigate('/portal/roadmap');
      return;
    }

    setTaskDetailsLoading(true);
    setTaskDetailsError(null);
    setSelectedTaskOrEvent(null);
    setTaskDetailsComments([]);
    try {
      let details = { ...item };
      let comments = [];
      if (type === 'task') {
        // Get comments for the task
        comments = await getTaskComments(item.id);
        // Get full assignee details for the task
        const assigneesWithDetails = (item.assignees || []).map(assigneeId => {
          const internalUser = internalUsers.find(u => u.id === assigneeId);
          if (internalUser) {
            return {
              ...internalUser,
              profileImage: internalUser.profileImage || null
            };
          }
          const account = accounts.find(a => a.id === assigneeId);
          if (account) {
            return {
              id: account.id,
              name: account.company,
              type: 'client',
              initials: account.company?.[0] || 'C',
              profileImage: null
            };
          }
          return null;
        }).filter(Boolean);
        details.assignees = Array.isArray(item.assignees) && item.assignees[0]?.name 
          ? item.assignees 
          : assigneesWithDetails;
      } else if (type === 'event') {
        // For events, you can add more detail fetching if needed
        details.assignees = [];
      }
      setSelectedTaskOrEvent({ ...details, type });
      setTaskDetailsComments(comments);
      setShowTaskDetailsModal(true);
    } catch (err) {
      setTaskDetailsError('Failed to load details. Please try again.');
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const handleAddTaskComment = async () => {
    if (!newTaskComment.trim() || !selectedTaskOrEvent) return;
    setIsAddingComment(true);
    try {
      const currentUserObj = internalUsers.find(u => u.id === currentUser?.uid);
      let fileData = {};
      if (commentFile) {
        // If you have a file upload function, use it here. Otherwise, just attach file name.
        // For now, just attach file name and URL as placeholder
        fileData = {
          fileName: commentFile.name,
          fileType: commentFile.type,
          fileUrl: URL.createObjectURL(commentFile) // Replace with real upload logic if needed
        };
      }
      const commentData = {
        message: newTaskComment.trim(),
        userName: currentUserObj?.name || 'Unknown User',
        userInitials: currentUserObj?.initials || 'UU',
        userProfileImage: currentUserObj?.profileImage || currentUserProfile?.profileImage || currentUser?.photoURL || null,
        ...fileData
      };
      const newCommentDoc = await addTaskComment(selectedTaskOrEvent.id, commentData);
      setTaskDetailsComments(prev => [...prev, newCommentDoc]);
      setNewTaskComment('');
      setCommentFile(null);
      setCommentFileError('');
    } catch (err) {
      setCommentFileError('Failed to add comment.');
    } finally {
      setIsAddingComment(false);
    }
  };

  // Get current month name for check-in title
  const getCurrentMonthName = () => {
    return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Handle check-in submission
  const handleCheckInSubmit = async () => {
    if (!currentUser || !checkInData.overallSatisfaction) {
      alert('Please rate your overall satisfaction.');
      return;
    }

    setSubmittingCheckIn(true);
    try {
      let followUps = { wantsSuccessManagerQuestion: false, requestedCheckInCall: false };
      try {
        followUps = {
          ...followUps,
          ...JSON.parse(sessionStorage.getItem(manualCheckInFollowUpsStorageKey()) || '{}'),
        };
      } catch (_) {
        followUps = { wantsSuccessManagerQuestion: false, requestedCheckInCall: false };
      }

      const checkInSubmission = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        overallSatisfaction: checkInData.overallSatisfaction,
        comments: checkInData.comments.trim(),
        month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        submittedAt: serverTimestamp(),
        createdAt: new Date().toISOString(),
        wantsSuccessManagerQuestion: !!followUps.wantsSuccessManagerQuestion,
        requestedCheckInCall: !!followUps.requestedCheckInCall,
      };

      // Save to Firestore check_ins collection
      await addDoc(collection(db, 'check_ins'), checkInSubmission);

      try {
        sessionStorage.removeItem(manualCheckInFollowUpsStorageKey());
      } catch (_) {}

      // Mark as submitted for this month
      setHasSubmittedThisMonth(true);

      // Reset form and close modal
      setCheckInData({
        overallSatisfaction: null,
        comments: ''
      });
      setShowCheckInModal(false);
    } catch (error) {
      console.error('Error submitting check-in:', error);
      alert('Failed to submit check-in. Please try again.');
    } finally {
      setSubmittingCheckIn(false);
    }
  };

  const updateCheckInData = (field, value) => {
    setCheckInData((prev) => ({ ...prev, [field]: value }));
  };

  // When portal is locked, the dashboard becomes the plan picker: program
  // cards (→ program detail → checkout) plus a check-in call scheduler.
  if (isPortalLocked) {
    return (
      <div ref={dashboardTopRef}>
        <PortalLockedDashboard />
      </div>
    );
  }

  return (
    <div ref={dashboardTopRef} className={`min-h-screen ${isMobile ? 'p-3' : 'p-6'}`}>
      {/* Same ambient BBA background the /start dashboard uses */}
      <PortalBackground />
      <div className="relative">
      {/* Header — greeting + date */}
      <div className={`relative mb-6 text-center ${isMobile ? 'pt-10' : ''}`}>
        <button
          type="button"
          onClick={openManualCheckInCalendly}
          title="Schedule a Check-In Call"
          className="absolute top-0 left-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm transition-shadow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md text-gray-700 dark:text-gray-200"
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">📞</span>
          {!isMobile && <span>Check-In Call</span>}
        </button>

        <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 dark:text-white`}>
          {(() => {
            const hour = new Date().getHours();
            const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
            const firstName =
              currentUserProfile?.firstName ||
              currentUser?.displayName?.split(' ')[0] ||
              '';
            return firstName ? `${greeting}, ${firstName}` : greeting;
          })()}
          <span className="ml-2" aria-hidden="true">👋</span>
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} — here's what's happening across your business.
        </p>
      </div>

      {/* Monthly check-in banner */}
      {shouldShowCheckInCard() && (
        <div className="mb-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm">
          <div className={`flex flex-wrap items-center justify-between gap-3 ${isMobile ? 'p-4' : 'px-6 py-4'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold">Your {getCurrentMonthName()} check-in is ready</p>
                <p className="text-sm text-white/80">Two minutes to tell us how things are going.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowCheckInModal(true)}
              className="px-4 py-2 rounded-lg bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50 transition-colors"
            >
              Complete Check In
            </button>
          </div>
        </div>
      )}

      {/* What's New + Strategy Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        {/* What's New */}
        <div className="lg:col-span-8 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col">
          <div className={`flex flex-wrap items-center justify-between gap-3 ${isMobile ? 'p-4 pb-3' : 'p-5 pb-4'}`}>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden="true">✨</span>
              What's New
            </h2>
          </div>
          <div className={`flex-1 ${isMobile ? 'px-2 pb-2' : 'px-3 pb-3'}`}>
            {isOnboardingMode ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Complete Onboarding to Unlock</p>
              </div>
            ) : whatsNewLoading ? (
              <div className="space-y-2 px-2 pb-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 animate-pulse">
                    <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                      <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              (() => {
                const filtered = whatsNewItems;
                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <Sparkles className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nothing new here yet</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">New courses, lessons, documents, and updates will show up here.</p>
                    </div>
                  );
                }
                const isFresh = (date) => {
                  const t = new Date(date).getTime();
                  return Number.isFinite(t) && Date.now() - t < 14 * 24 * 60 * 60 * 1000;
                };
                return (
                  <div className="space-y-0.5">
                    {filtered.slice(0, 8).map((item) => {
                      const meta = WHATS_NEW_KIND_META[item.kind] || WHATS_NEW_KIND_META.update;
                      const onOpen = item.path
                        ? () => navigate(item.path)
                        : item.url
                        ? () => window.open(item.url, '_blank', 'noopener,noreferrer')
                        : null;
                      const content = (
                        <>
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                            <span className="text-xl leading-none" aria-hidden="true">{item.emoji || meta.emoji}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{item.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {meta.label}
                              {item.subtitle ? ` · ${item.subtitle}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {isFresh(item.date) && (
                              <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                                New
                              </span>
                            )}
                            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{formatTimeAgo(item.date)}</span>
                            {onOpen && <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />}
                          </div>
                        </>
                      );
                      return onOpen ? (
                        <button
                          key={item.id}
                          type="button"
                          onClick={onOpen}
                          className="w-full text-left flex items-center gap-3 rounded-lg px-2.5 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          {content}
                        </button>
                      ) : (
                        <div key={item.id} className="flex items-center gap-3 rounded-lg px-2.5 py-2.5">
                          {content}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Strategy Calls + Upcoming Calls */}
        <div
          className={`lg:col-span-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col ${
            isOnboardingMode ? 'opacity-60' : ''
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden="true">📞</span>
              Implementation Hours
            </h2>
            {!isOnboardingMode && strategyCallsPeriodEnd && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                {(() => {
                  const now = new Date();
                  now.setHours(0, 0, 0, 0);
                  const end = new Date(strategyCallsPeriodEnd);
                  end.setHours(0, 0, 0, 0);
                  const daysLeft = Math.max(0, Math.ceil((end - now) / (24 * 60 * 60 * 1000)));
                  return `Resets in ${daysLeft}d`;
                })()}
              </span>
            )}
          </div>
          {isOnboardingMode ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Complete Onboarding to Unlock</p>
            </div>
          ) : strategyCallsLoading ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <BBALoader size={32} />
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              {/* Hours remaining — large display */}
              <div className="flex flex-col items-center text-center py-1">
                {(() => {
                  const remaining = Math.max(0, (strategyCallsLimit ?? 1) - (strategyCallsUsed ?? 0));
                  return (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-bold text-gray-900 dark:text-white leading-none">
                          {remaining}
                        </span>
                        <span className="text-2xl font-semibold text-gray-400 dark:text-gray-500 leading-none">
                          / {strategyCallsLimit ?? 1}
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                        {remaining === 1 ? 'Hour remaining' : 'Hours remaining'} this period
                      </p>
                      <div className="mt-3 w-full max-w-[220px] h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-red-500 to-rose-500 transition-all duration-300"
                          style={{
                            width: `${Math.min(100, ((strategyCallsUsed ?? 0) / Math.max(1, strategyCallsLimit ?? 1)) * 100)}%`,
                          }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Upcoming calls */}
              <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  Upcoming Calls
                </p>
                {eventsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <BBALoader size={24} />
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No upcoming calls scheduled</p>
                ) : (
                  <div className="space-y-1">
                    {filteredEvents.slice(0, 3).map((event) => {
                      const eventTime = convertToDate(event.startTime || event.start);
                      const isGoogleEvent = event.isGoogleEvent || event.source === 'google';
                      return (
                        <button
                          key={event.id}
                          type="button"
                          onClick={() => handleTaskOrEventClick(event, 'event')}
                          className="w-full text-left flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${isGoogleEvent ? 'bg-blue-500' : 'bg-red-500'}`}></span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{event.title}</p>
                            {eventTime && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {eventTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} at{' '}
                                {eventTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors"
                onClick={() => {
                  setStrategyCallsQuantity(1);
                  setStrategyCallsCheckoutError(null);
                  setShowStrategyCallsPurchaseModal(true);
                }}
              >
                Add More Hours
              </button>
            </div>
          )}
          {/* Success Manager footer */}
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
            {strategyCallsSuccessManager?.profileImage ? (
              <img
                src={strategyCallsSuccessManager.profileImage}
                alt={strategyCallsSuccessManager.name || 'Success Manager'}
                className="w-9 h-9 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-semibold shrink-0">
                {strategyCallsSuccessManager?.initials || 'IR'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {strategyCallsSuccessManager?.name || 'Ian Riddle'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Your Success Manager</p>
            </div>
            <a
              href="tel:4196909665"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              419-690-9665
            </a>
          </div>
        </div>
      </div>

      {/* Next Breakthrough Session + Community / Summit */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        {/* Next Breakthrough Session — hero */}
        <div
          className="lg:col-span-4 relative overflow-hidden rounded-xl text-white shadow-sm"
          style={{
            background: getBreakthroughTheme(
              noBreakthroughThisWeek ? 'Sales' : nextBreakthroughSession?.department
            ).gradient,
          }}
        >
          <BreakthroughSessionBackground
            department={noBreakthroughThisWeek ? 'Sales' : nextBreakthroughSession?.department}
          />
          <div className={`relative z-10 ${isMobile ? 'p-5' : 'p-7'} flex flex-col h-full`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/70">
              <Video className="w-4 h-4" />
              <span>Next Breakthrough Session</span>
              {breakthroughCountdown?.live && (
                <span className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-2 py-0.5 text-[10px] font-bold text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  LIVE NOW
                </span>
              )}
            </div>
            {breakthroughLoading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            ) : noBreakthroughThisWeek ? (
              <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                <CalendarOff className="w-10 h-10 text-white/60 mb-3" />
                <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>No Class This Week</p>
              </div>
            ) : nextBreakthroughSession ? (
              <div className={`mt-4 flex flex-wrap items-center ${isMobile ? 'gap-4' : 'gap-6'} flex-1`}>
                <div className="flex-1 min-w-[220px]">
                  <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold leading-snug`}>
                    {nextBreakthroughSession.eventName || 'Breakthrough Session'}
                  </h2>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/85">
                    {formatSessionDateLabel(nextBreakthroughSession) && (
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="w-4 h-4" />
                        {formatSessionDateLabel(nextBreakthroughSession)}
                      </span>
                    )}
                    {formatSessionTimeLabel(nextBreakthroughSession) && (
                      <span className="inline-flex items-center gap-1.5">
                        <FaClock className="w-3.5 h-3.5" />
                        {formatSessionTimeLabel(nextBreakthroughSession)}
                      </span>
                    )}
                  </div>
                  {nextBreakthroughSession.presenter && (
                    <div className="mt-4 flex items-center gap-3">
                      {nextBreakthroughSession.presenterPhoto ? (
                        <img
                          src={nextBreakthroughSession.presenterPhoto}
                          alt={nextBreakthroughSession.presenter}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-white/40"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-sm font-semibold">
                          {initialsFor(nextBreakthroughSession.presenter)}
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-white/70">Presented by</p>
                        <p className="text-sm font-semibold">{nextBreakthroughSession.presenter}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-center gap-3">
                  {breakthroughCountdown && !breakthroughCountdown.live && !breakthroughCountdown.past && (
                    <div className="flex items-center gap-2">
                      {[
                        [breakthroughCountdown.days, 'Days'],
                        [breakthroughCountdown.hours, 'Hrs'],
                        [breakthroughCountdown.minutes, 'Min'],
                      ].map(([value, unit]) => (
                        <div key={unit} className="w-14 rounded-lg bg-white/15 backdrop-blur-sm py-2 text-center">
                          <p className="text-xl font-bold leading-none">{value}</p>
                          <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-white/70">{unit}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      window.open(BREAKTHROUGH_SESSION_MEET_URL, '_blank', 'noopener,noreferrer')
                    }
                    className="w-full px-5 py-2.5 rounded-lg bg-white text-sm font-bold hover:bg-white/90 transition-colors shadow"
                    style={{ color: getBreakthroughTheme(nextBreakthroughSession?.department).accent }}
                  >
                    {breakthroughCountdown?.live ? 'Join Live Session' : 'Join Session'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                <Video className="w-10 h-10 text-white/50 mb-3" />
                <p className="font-semibold">No upcoming sessions scheduled</p>
                <p className="text-sm text-white/70 mt-1">Check back soon — new breakthrough sessions are added regularly.</p>
              </div>
            )}
          </div>
        </div>

        {/* My Tasks */}
        <div
          className={`lg:col-span-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col ${
            isOnboardingMode && !onboardingProgress.tasksUnlocked ? 'opacity-60' : ''
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden="true">✅</span>
              My Tasks
            </h2>
            {!(isOnboardingMode && !onboardingProgress.tasksUnlocked) && filteredTasks.length > 0 && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                {filteredTasks.length} open
              </span>
            )}
          </div>
          {isOnboardingMode && !onboardingProgress.tasksUnlocked ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Complete Onboarding to Unlock</p>
            </div>
          ) : tasksLoading ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <BBALoader size={32} />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <img src="/caught-up.svg" alt="All caught up" className="w-12 h-12 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">You're all caught up!</p>
            </div>
          ) : (
            <div className="flex-1 space-y-1.5">
              {filteredTasks.slice(0, 5).map((task) => {
                const dueDate = convertToDate(task.dueDate);
                const isOverdue = dueDate && dueDate < new Date() && !task.completed;
                return (
                  <div
                    key={task.id}
                    className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => handleTaskOrEventClick(task, 'task')}
                  >
                    <button
                      onClick={(e) => handleTaskCompletion(task.id, e)}
                      className="mt-0.5 w-5 h-5 rounded-full border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 flex items-center justify-center shrink-0 hover:border-red-500 transition-colors"
                    >
                      {task.completed && <Check size={14} className="text-white" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {task.title}
                      </p>
                      {task.isRoadmapTask && task.roadmapTemplateTitle ? (
                        <p className="text-xs text-red-600 dark:text-red-400 truncate">
                          Roadmap · {task.roadmapTemplateTitle}
                        </p>
                      ) : dueDate ? (
                        <p className={`text-xs ${isOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          Due {dueDate.toLocaleDateString()}
                          {isOverdue && ' · Overdue'}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!(isOnboardingMode && !onboardingProgress.tasksUnlocked) && (
            <button
              type="button"
              onClick={() => navigate('/portal/tasks')}
              className="mt-4 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              View All Tasks
            </button>
          )}
        </div>

        {/* Community + Summit */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          {/* Community */}
          <div
            className={`flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col ${
              isOnboardingMode && !onboardingProgress.communityUnlocked ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-base leading-none" aria-hidden="true">💬</span>
                Community
              </h2>
              {!(isOnboardingMode && !onboardingProgress.communityUnlocked) && (
                <button
                  type="button"
                  onClick={() => navigate('/portal/community')}
                  className="text-xs font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 inline-flex items-center gap-0.5"
                >
                  View all
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {isOnboardingMode && !onboardingProgress.communityUnlocked ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Complete Onboarding to Unlock</p>
              </div>
            ) : communityPostsLoading ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
              </div>
            ) : recentCommunityPosts.length > 0 ? (
              <div className="flex-1 space-y-1">
                {recentCommunityPosts.slice(0, 3).map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    onClick={() => navigate('/portal/community')}
                    className="w-full text-left flex items-start gap-2.5 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {post.authorAvatar ? (
                      <img src={post.authorAvatar} alt={post.author} className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">
                        {initialsFor(post.author) || 'BB'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{post.title || 'Community post'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {post.author || 'Unknown'} · {formatTimeAgo(post.createdAt)}
                      </p>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          {post.likes || 0}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {post.comments || 0}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <Users className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No posts yet — start the conversation!</p>
              </div>
            )}
          </div>

          {/* BBA Summit — compact (hidden when turned off in CRM ▸ Summit Overview) */}
          {showSummitCard && (
          <div
            className="relative rounded-xl shadow-sm cursor-pointer transition-all hover:shadow-md text-white overflow-hidden"
            style={{ background: getBreakthroughTheme('Summit').gradient }}
            onClick={() => navigate('/portal/summit')}
          >
            <BreakthroughSessionBackground department="Summit" />
            <div className={`relative z-10 flex items-center justify-between gap-3 ${isMobile ? 'p-4' : 'p-5'}`}>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/75">BBA Summit</p>
                <p className="text-2xl font-bold mt-0.5 truncate">
                  {summitLoading
                    ? '…'
                    : (() => {
                        const daysUntil = getDaysUntilSummit();
                        if (daysUntil === null) return formatSummitDates();
                        if (daysUntil <= 0) return 'Happening now!';
                        return `${daysUntil} day${daysUntil === 1 ? '' : 's'} away`;
                      })()}
                </p>
                <p className="text-xs text-white/80 mt-0.5">{summitLoading ? 'Loading…' : formatSummitDates()}</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className="px-2.5 py-1 rounded-full bg-white/20 border border-white/30 text-[11px] font-semibold whitespace-nowrap">
                  {registrationLoading ? '…' : isRegistered ? 'Registered ✓' : 'Not Registered'}
                </span>
                <ChevronRight className="w-4 h-4 text-white/70" />
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Roadmap + My Events */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
      {/* Roadmap */}
      <div
        className={`lg:col-span-8 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-6'} flex flex-col`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-base leading-none" aria-hidden="true">🗺️</span>
            Your Roadmap
          </h2>
          <div className="flex items-center gap-3">
            {roadmapItems.length > 0 && (
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {roadmapItems.filter((i) => i.status === 'completed').length} of {roadmapItems.length} milestones complete
              </span>
            )}
            <button
              type="button"
              onClick={() => navigate('/portal/roadmap')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 transition-opacity inline-flex items-center gap-1"
            >
              View Full Roadmap
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {roadmapItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <MapIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Your personalized roadmap is being prepared</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Your team will build out your milestones with you.</p>
          </div>
        ) : (
          <>
            {(() => {
              const completedCount = roadmapItems.filter((i) => i.status === 'completed').length;
              const pct = Math.round((completedCount / roadmapItems.length) * 100);
              const currentStep =
                roadmapItems.find((i) => i.status === 'in-progress') ||
                roadmapItems.find((i) => i.status !== 'completed') ||
                roadmapItems[roadmapItems.length - 1];
              const currentIdx = roadmapItems.findIndex((i) => i.id === currentStep.id);
              const allDone = completedCount === roadmapItems.length;
              return (
                <div className="flex-1 flex flex-col">
                  {/* Overall progress */}
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 w-10 text-right">{pct}%</span>
                  </div>

                  {/* Current step spotlight */}
                  <div className="flex items-center gap-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                        allDone
                          ? 'bg-emerald-500 text-white'
                          : 'bg-red-600 text-white ring-4 ring-red-100 dark:ring-red-900/40'
                      }`}
                    >
                      {allDone ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <span className="text-lg font-bold">{currentIdx + 1}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                        {allDone ? 'Completed' : `Step ${currentIdx + 1} of ${roadmapItems.length} · In Progress`}
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {allDone ? 'All milestones complete 🎉' : currentStep.title}
                      </p>
                    </div>
                  </div>

                  {/* Up next tasks */}
                  {roadmapDashboardTasks.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                        Up next · {roadmapDashboardTasks[0].roadmapTemplateTitle}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {roadmapDashboardTasks.slice(0, 4).map((task) => (
                          <button
                            key={task.id}
                            type="button"
                            onClick={() => navigate('/portal/roadmap')}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                            {task.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>

        {/* Goal Tracking */}
        <div
          className={`lg:col-span-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-5'} flex flex-col ${
            isOnboardingMode ? 'opacity-60' : ''
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden="true">🎯</span>
              Goal Tracking
            </h2>
            {!isOnboardingMode && (
              <button
                type="button"
                onClick={() => setShowGoalSelectionModal(true)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Customize Goals"
              >
                <span className="text-lg">📌</span>
              </button>
            )}
          </div>
          {isOnboardingMode ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Complete Onboarding to Unlock</p>
            </div>
          ) : (goalsLoading || salesMarketingLoading) ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : selectedGoals.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <Target className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No goals pinned</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Tap 📌 to choose goals to track</p>
            </div>
          ) : (
            <div className="flex-1 space-y-4">
              {selectedGoals.map((goalKey, index) => {
                const [department, goalId] = goalKey.split('_');
                const departmentKey = Object.keys(availableGoals).find(
                  (key) => key.toLowerCase() === department.toLowerCase()
                );
                const goal = availableGoals[departmentKey]?.find((g) => g.id === parseInt(goalId));
                if (!goal) return null;
                const colors = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];
                const color = colors[index % colors.length];
                const current = goal.actual || 0;
                const target = goal.target || 100;
                const pct = Math.min(100, Math.round((current / Math.max(1, target)) * 100));
                const fmt = (v) =>
                  goal.unit === 'usd'
                    ? `$${v.toLocaleString()}`
                    : goal.unit === 'percent'
                    ? `${v}%`
                    : v;
                return (
                  <div key={goalKey}>
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{goal.metricName}</span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white shrink-0">
                        {fmt(current)} <span className="text-gray-400 dark:text-gray-500 font-medium">/ {fmt(target)}</span>
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tasks Instruction Message */}
      {showTasksInstruction && (
        <div className="mb-6">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-emerald-800 dark:text-emerald-200 font-medium mb-1">
                  Tasks Added Successfully!
                </h3>
                <p className="text-emerald-700 dark:text-emerald-300 text-sm">
                  Your onboarding tasks have been added to your task list. Click the Tasks card above to explore and manage your new tasks.
                </p>
              </div>
              <button
                onClick={() => setShowTasksInstruction(false)}
                className="text-emerald-500 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Calls Purchase Modal */}
      <Modal isOpen={showStrategyCallsPurchaseModal} onClose={() => setShowStrategyCallsPurchaseModal(false)}>
        <div className="w-full max-w-md">
          <h2 className="text-xl font-bold dark:text-white mb-4 text-center">Add Additional Hours</h2>
          <div className="mb-4 flex flex-col items-center">
            <label className="block text-sm font-medium dark:text-gray-300 mb-2">Quantity</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStrategyCallsQuantity(q => Math.max(1, q - 1))}
                className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                −
              </button>
              <span className="text-lg font-semibold dark:text-white w-8 text-center">{strategyCallsQuantity}</span>
              <button
                type="button"
                onClick={() => setStrategyCallsQuantity(q => Math.min(10, q + 1))}
                className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                +
              </button>
            </div>
          </div>
          <div className="mb-4 p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Total</span>
              <span className="font-semibold dark:text-white">${(300 * strategyCallsQuantity).toLocaleString()}</span>
            </div>
          </div>
          {strategyCallsCheckoutError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">{strategyCallsCheckoutError}</p>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => handlePurchaseStrategyCalls('ach')}
              disabled={strategyCallsCheckoutLoading}
              className="flex-1 py-3 px-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-2"
            >
              <Landmark className="w-6 h-6" />
              <span>Pay with Bank Account (ACH)</span>
            </button>
            <button
              onClick={() => handlePurchaseStrategyCalls('card')}
              disabled={strategyCallsCheckoutLoading}
              className="flex-1 py-3 px-4 rounded-lg border-2 border-red-500 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-2"
            >
              <CreditCard className="w-6 h-6" />
              <span>Pay with Credit Card (3% Fee)</span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Goal Selection Modal */}
      <Modal isOpen={showGoalSelectionModal} onClose={() => setShowGoalSelectionModal(false)}>
        <div className="w-full max-w-6xl">
          <div className="flex justify-center items-center mb-6">
            <h2 className="text-2xl font-bold dark:text-white">Pin Goals to Dashboard</h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              Select up to 3 goals to pin to your Goal Tracking section ({selectedGoals.length}/3 selected)
            </p>
            
            {/* Department Tabs */}
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg mb-6">
              {['Sales', 'Operations', 'Finance', 'Technology', 'Marketing'].map((department) => (
                <button
                  key={department}
                  onClick={() => setActiveTab(department)}
                  className={`flex-1 px-2 py-2 text-xs font-medium rounded-md transition-colors ${
                    activeTab === department
                      ? 'bg-white dark:bg-gray-800 shadow-sm dark:text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                  }`}
                >
                  {department}
                </button>
              ))}
            </div>
            
            {/* Goals List for Active Department */}
            <div className="max-h-96 overflow-y-auto">
              {availableGoals[activeTab]?.length > 0 ? (
                <div className="space-y-2">
                  {availableGoals[activeTab].map((goal) => {
                    const goalKey = `${activeTab.toLowerCase()}_${goal.id}`;
                    const isSelected = selectedGoals.includes(goalKey);
                    const canSelect = isSelected || selectedGoals.length < 3;
                    
                    return (
                      <div
                        key={goal.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : canSelect
                            ? 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => {
                          if (!canSelect) return;
                          
                          if (isSelected) {
                            setSelectedGoals(selectedGoals.filter(g => g !== goalKey));
                          } else {
                            setSelectedGoals([...selectedGoals, goalKey]);
                          }
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!canSelect}
                            onChange={() => {}}
                            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium dark:text-white">{goal.metricName}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{goal.description}</p>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-400">
                                Target: {goal.unit === 'usd' ? `$${goal.target?.toLocaleString()}` : 
                                        goal.unit === 'percent' ? `${goal.target}%` : 
                                        goal.target}
                              </span>
                              <span className="text-xs text-gray-400">
                                Current: {goal.unit === 'usd' ? `$${goal.actual?.toLocaleString()}` : 
                                         goal.unit === 'percent' ? `${goal.actual}%` : 
                                         goal.actual}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No goals found for {activeTab} department
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {selectedGoals.length} of 3 goals selected
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowGoalSelectionModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Save to current user's doc (company pinned goals are read from primary user via goalsUserId)
                    if (currentUser) {
                      try {
                        await updateDoc(doc(db, 'users', currentUser.uid), {
                          pinnedGoals: selectedGoals
                        });
                        localStorage.setItem(`selectedGoals_${currentUser.uid}`, JSON.stringify(selectedGoals));
                      } catch (err) {
                        console.error('Error saving pinned goals to Firebase:', err);
                        // Fallback to localStorage only
                        localStorage.setItem(`selectedGoals_${currentUser.uid}`, JSON.stringify(selectedGoals));
                      }
                    }
                    setShowGoalSelectionModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Pin Selected Goals
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Check In Modal */}
      <Modal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        maxWidth="2xl"
        showCloseButton={false}
        closeOnBackdropClick={false}
      >
        <div className="w-full max-h-[80vh] overflow-y-auto">
          <div className="flex justify-center items-center mb-6 relative">
            <h2 className="text-2xl font-bold dark:text-white">{getCurrentMonthName()} Check In</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                How satisfied are you with our overall service?{' '}
                <span className="text-red-600 dark:text-red-400" aria-hidden>
                  *
                </span>
              </label>
              <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => updateCheckInData('overallSatisfaction', star)}
                    className={`text-4xl sm:text-5xl leading-none p-1 rounded transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      (checkInData.overallSatisfaction || 0) >= star
                        ? 'text-yellow-400'
                        : 'text-gray-200 dark:text-gray-600'
                    }`}
                    aria-label={`${star} out of 5 stars`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Feedback
              </label>
              <textarea
                value={checkInData.comments}
                onChange={(e) => updateCheckInData('comments', e.target.value)}
                placeholder="Optional — share any additional thoughts..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCheckInSuccessManagerQuestionModalOpen(true)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                I Have A Question
              </button>
              <button
                type="button"
                onClick={() => {
                  persistManualCheckInFollowUp({ requestedCheckInCall: true });
                  openManualCheckInCalendly();
                }}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                I&apos;d Like to Set Up a Check In Call
              </button>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCheckInSubmit}
                disabled={submittingCheckIn || !checkInData.overallSatisfaction}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  submittingCheckIn || !checkInData.overallSatisfaction
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {submittingCheckIn ? 'Submitting...' : 'Submit Check In'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <SuccessManagerQuestionModal
        isOpen={checkInSuccessManagerQuestionModalOpen}
        onClose={() => setCheckInSuccessManagerQuestionModalOpen(false)}
        onSubmit={handleCheckInSuccessManagerQuestionSubmit}
        assigneeName={successManagerQuestionAssignee?.name}
      />

      {/* Task/Event Details Modal */}
      {showTaskDetailsModal && selectedTaskOrEvent && (
        <Modal isOpen={showTaskDetailsModal} onClose={() => setShowTaskDetailsModal(false)} maxWidth={selectedTaskOrEvent.type === 'event' ? '2xl' : 'lg'}>
          <div>
            {selectedTaskOrEvent.type === 'task' ? (
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white mb-4">
                Task Details
              </h2>
            ) : null}
            {taskDetailsLoading ? (
              <div className="text-center text-gray-500 dark:text-gray-400">Loading...</div>
            ) : taskDetailsError ? (
              <div className="text-center text-red-500">{taskDetailsError}</div>
            ) : (
              <div>
                {selectedTaskOrEvent.type === 'task' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                      <div className="text-lg text-gray-900 dark:text-white">{selectedTaskOrEvent.title}</div>
                    </div>
                    {selectedTaskOrEvent.description && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <div className="text-gray-600 dark:text-gray-400">{selectedTaskOrEvent.description}</div>
                      </div>
                    )}
                    {selectedTaskOrEvent.companyName && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                        <div className="text-gray-600 dark:text-gray-400">{selectedTaskOrEvent.companyName}</div>
                      </div>
                    )}
                    {(selectedTaskOrEvent.source === 'fathom' || selectedTaskOrEvent.fathomRecordingId) && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Created by</label>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <img src="/fathomlogo.png" alt="Fathom" className="w-6 h-6 object-contain" />
                          <span>Fathom</span>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Due Date</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.dueDate ? new Date(selectedTaskOrEvent.dueDate).toLocaleDateString() : 'No due date'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Estimated Time</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.estimatedHours || selectedTaskOrEvent.estimatedMinutes 
                            ? `${selectedTaskOrEvent.estimatedHours || 0}h ${selectedTaskOrEvent.estimatedMinutes || 0}m`
                            : 'No time estimate'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">Attachments</label>
                        <div className="text-center text-gray-600 dark:text-gray-400">
                          {selectedTaskOrEvent.files?.length > 0 
                            ? selectedTaskOrEvent.files.map((file, idx) => <div key={idx}>{file.name}</div>)
                            : 'No attachments'}
                        </div>
                      </div>
                    </div>
                                          <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignees</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedTaskOrEvent.assignees?.map(user => (
                            <div key={user.id} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                              <UserAvatar 
                                user={user}
                                size="sm"
                              />
                              <span className="text-sm text-gray-900 dark:text-white">{user.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    <div className="mt-6 border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Comments</h3>
                      <div className="space-y-4 mb-4 max-h-60 overflow-y-auto">
                        {taskDetailsComments.length > 0 ? taskDetailsComments.map(comment => (
                          <div key={comment.id} className="flex gap-3">
                            <UserAvatar 
                              user={{
                                name: comment.userName,
                                initials: comment.userInitials,
                                profileImage: comment.userProfileImage
                              }}
                              size="md"
                              className="flex-shrink-0"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{comment.userName}</span>
                                  <span className="text-sm text-gray-500">
                                    {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
                                  </span>
                                </div>
                              </div>
                              <p className="text-gray-700">{comment.message}</p>
                              {comment.fileUrl && (
                                <div className="mt-2">
                                  <a 
                                    href={comment.fileUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600"
                                  >
                                    {comment.fileName}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )) : <div className="text-gray-500">No comments</div>}
                      </div>
                    </div>
                  </>
                )}
                {selectedTaskOrEvent.type === 'event' && (
                  <div className="space-y-4">
                    <div className="relative mb-6">
                      <h2 className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {(() => {
                          const eventTitle = selectedTaskOrEvent.title || 'Event Details';
                          return eventTitle.replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/, '');
                        })()}
                      </h2>
                    </div>
                    <div className="flex flex-wrap gap-6 items-start">
                      {(selectedTaskOrEvent.startTime || selectedTaskOrEvent.start) && (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-center space-x-2 mb-2">
                            <FaCalendarAlt className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date & Time</h3>
                          </div>
                          <div className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            <p>{format(new Date(selectedTaskOrEvent.startTime || selectedTaskOrEvent.start), 'EEEE, MMMM d, yyyy')}</p>
                            <div className="flex items-center justify-center space-x-2 mt-1">
                              <FaClock className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                              <p>
                                {format(new Date(selectedTaskOrEvent.startTime || selectedTaskOrEvent.start), 'h:mm a')}
                                {(selectedTaskOrEvent.endTime || selectedTaskOrEvent.end) && ` - ${format(new Date(selectedTaskOrEvent.endTime || selectedTaskOrEvent.end), 'h:mm a')}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {(selectedTaskOrEvent.source === 'internal_google' || selectedTaskOrEvent.isGoogleEvent) && selectedTaskOrEvent.organizer && (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-center space-x-2 mb-2">
                            <FaUsers className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Organizer</h3>
                          </div>
                          <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {selectedTaskOrEvent.organizer.displayName || selectedTaskOrEvent.organizer.email || 'Unknown'}
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedTaskOrEvent.location && (
                      <div>
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <FaMapMarkerAlt className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                          <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</h3>
                        </div>
                        <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedTaskOrEvent.location}</p>
                      </div>
                    )}
                    {selectedTaskOrEvent.description && (
                      <div>
                        <h3 className={`font-semibold mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</h3>
                        <p className={`whitespace-pre-wrap text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {selectedTaskOrEvent.description}
                        </p>
                      </div>
                    )}
                    {(selectedTaskOrEvent.htmlLink || selectedTaskOrEvent.hangoutLink || selectedTaskOrEvent.googleMeetLink) && (
                      <div className="pt-4 flex flex-wrap gap-3 justify-center">
                        {selectedTaskOrEvent.htmlLink && (
                          <a
                            href={selectedTaskOrEvent.htmlLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <FaCalendarAlt />
                            <span>Open in Google Calendar</span>
                          </a>
                        )}
                        {(selectedTaskOrEvent.hangoutLink || selectedTaskOrEvent.googleMeetLink) && (
                          <a
                            href={selectedTaskOrEvent.hangoutLink || selectedTaskOrEvent.googleMeetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors bg-green-600 hover:bg-green-700 text-white"
                          >
                            <FaUsers />
                            <span>Join Google Meet</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {selectedTaskOrEvent.type === 'task' && (
                <div className="flex gap-2 mt-2">
                  <UserAvatar 
                    user={internalUsers.find(u => u.id === currentUser?.uid) || { 
                      initials: currentUserProfile?.firstName && currentUserProfile?.lastName
                        ? `${currentUserProfile.firstName[0]}${currentUserProfile.lastName[0]}`.toUpperCase()
                        : currentUserProfile?.firstName
                        ? currentUserProfile.firstName.substring(0, 2).toUpperCase()
                        : (currentUser?.displayName || currentUser?.email?.split('@')[0] || 'UU').substring(0, 2).toUpperCase(),
                      name: currentUserProfile?.firstName && currentUserProfile?.lastName
                        ? `${currentUserProfile.firstName} ${currentUserProfile.lastName}`
                        : currentUserProfile?.firstName
                        ? currentUserProfile.firstName
                        : currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown User',
                      profileImage: currentUserProfile?.profileImage || currentUser?.photoURL
                    }}
                    size="md"
                    className="flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="relative flex flex-col gap-2">
                      <textarea
                        value={newTaskComment}
                        onChange={e => setNewTaskComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="w-full border rounded-md p-2 pr-20 text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500"
                        rows={2}
                        disabled={isAddingComment}
                      />
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer text-gray-400 hover:text-gray-600 flex items-center gap-1">
                          <input
                            type="file"
                            className="hidden"
                            onChange={e => {
                              if (e.target.files[0] && e.target.files[0].size <= 10 * 1024 * 1024) {
                                setCommentFile(e.target.files[0]);
                                setCommentFileError('');
                              } else {
                                setCommentFileError('File must be less than 10MB');
                              }
                            }}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                          />
                          <Paperclip size={18} />
                          <span>Attach</span>
                        </label>
                        {commentFile && (
                          <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                            <span className="text-xs">{commentFile.name}</span>
                            <button onClick={() => setCommentFile(null)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                          </div>
                        )}
                        <button
                          onClick={handleAddTaskComment}
                          disabled={!newTaskComment.trim() || isAddingComment}
                          className={`p-1 rounded-full ml-auto ${
                            newTaskComment.trim() ? 'text-red-500 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'
                          }`}
                        >
                          <Send size={18} />
                        </button>
                      </div>
                      {commentFileError && <div className="text-xs text-red-500">{commentFileError}</div>}
                    </div>
                  </div>
                </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      </div>
    </div>
  );
};

export default ClientDashboard; 